import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput, OfficeTextarea } from "@/pages/admin/officeUi";
import { ESITI, esitoLabel, tipologiaLabel, fmtDate, fmtEur, CompensoPill } from "@/pages/partner/labels";

const sel = "rounded-xl border px-3 py-2 text-sm";

function Toggle({ label, checked, onChange, at, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[#D9501F]" data-testid={testId} />
      <span>{label}{checked && at ? <span style={{ color: OFFICE.muted }}> · {fmtDate(at)}</span> : null}</span>
    </label>
  );
}

function Editor({ r, onSaved }) {
  const [act, setAct] = useState(r.activation_date || "");
  const [canone, setCanone] = useState(r.canone_mensile ?? "");
  const [notes, setNotes] = useState(r.admin_notes || "");
  const patch = async (body, msg = "Aggiornato") => {
    try { await api.adminUpdateReferral(r.id, body); toast.success(msg); onSaved(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mt-4 pt-4 border-t space-y-4" style={{ borderColor: OFFICE.border }} data-testid={`referral-editor-${r.id}`}>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div>Tel: <a href={`tel:${r.phone}`} className="underline" data-testid={`referral-phone-${r.id}`}>{r.phone}</a></div>
        <div>Email: {r.email || "—"}</div>
        <div>Tipologia: {tipologiaLabel(r.tipologia)}</div>
      </div>
      {r.note ? <p className="text-sm">Nota partner: “{r.note}”</p> : null}
      {r.partner_notes?.map((n, i) => <p key={i} className="text-xs pl-3 border-l-2" style={{ borderColor: OFFICE.brand }}>{fmtDate(n.created_at)} · {n.text}</p>)}
      <div className="flex flex-wrap gap-5">
        <Toggle label="Contattato" checked={r.contacted} at={r.contacted_at} onChange={(v) => patch({ contacted: v })} testId={`referral-contacted-${r.id}`} />
        <Toggle label="Primo canone pagato" checked={r.first_canone_paid} at={r.first_canone_paid_at} onChange={(v) => patch({ first_canone_paid: v })} testId={`referral-first-paid-${r.id}`} />
        <Toggle label="Compenso pagato" checked={r.compenso_paid} at={r.compenso_paid_at} onChange={(v) => patch({ compenso_paid: v })} testId={`referral-compenso-paid-${r.id}`} />
      </div>
      <OfficeField label="Esito">
        <select value={r.esito} onChange={(e) => patch({ esito: e.target.value }, "Esito aggiornato")} className={`${sel} w-full`} style={{ borderColor: OFFICE.border }} data-testid={`referral-esito-select-${r.id}`}>
          {ESITI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </OfficeField>
      {r.esito === "yu_follow" ? (
        <div className="rounded-xl p-4 space-y-3" style={{ background: OFFICE.brandTertiary }}>
          <div className="grid sm:grid-cols-2 gap-3">
            <OfficeField label="Data attivazione"><OfficeInput type="date" value={act} onChange={(e) => setAct(e.target.value)} data-testid={`referral-activation-${r.id}`} /></OfficeField>
            <OfficeField label="Canone mensile (€)"><OfficeInput type="number" min="0" step="1" value={canone} onChange={(e) => setCanone(e.target.value)} data-testid={`referral-canone-${r.id}`} /></OfficeField>
          </div>
          <OfficeButton onClick={() => patch({ activation_date: act || null, canone_mensile: canone === "" ? null : Number(canone) })} data-testid={`referral-follow-save-${r.id}`}>Salva attivazione</OfficeButton>
          <p className="text-sm flex items-start gap-2" data-testid={`referral-convert-hint-${r.id}`}>
            <Building2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: OFFICE.brand }} />
            Promemoria: crea la struttura “{r.structure_name}” dal pannello e attiva l’accesso cliente. La segnalazione resta collegata al partner {r.partner_name}.
          </p>
        </div>
      ) : null}
      <OfficeField label="Note interne (visibili solo a te)">
        <OfficeTextarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`referral-admin-notes-${r.id}`} />
      </OfficeField>
      <OfficeButton variant="ghost" onClick={() => patch({ admin_notes: notes }, "Note salvate")} data-testid={`referral-notes-save-${r.id}`}>Salva note</OfficeButton>
      {r.log?.length ? (
        <details className="text-xs" data-testid={`referral-log-${r.id}`}>
          <summary className="cursor-pointer" style={{ color: OFFICE.muted }}>Registro modifiche ({r.log.length})</summary>
          <ul className="mt-2 space-y-1">
            {[...r.log].reverse().map((l, i) => (
              <li key={i}>{fmtDate(l.at)} {new Date(l.at?.endsWith?.("Z") || /[+]/.test(l.at || "") ? l.at : `${l.at}Z`).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {l.actor} · {l.field ? `${l.field}: ${String(l.old ?? "—")} → ${String(l.new ?? "—")}` : ({ created: "segnalazione inserita", note_added: "nota partner aggiunta" }[l.action] || l.action)}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Row({ r, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <OfficeCard className="!p-4" data-testid={`admin-referral-${r.id}`}>
      <button className="w-full flex flex-wrap items-center justify-between gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`admin-referral-toggle-${r.id}`}>
        <div className="min-w-0">
          <div className="font-medium">{r.structure_name} <span className="font-normal text-sm" style={{ color: OFFICE.muted }}>· {r.comune}</span></div>
          <div className="text-sm" style={{ color: OFFICE.muted }}>{fmtDate(r.created_at)} · {r.owner_name} · da {r.partner_name || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs" data-testid={`admin-referral-esito-${r.id}`}>{r.contacted ? "Contattato · " : ""}{esitoLabel(r.esito)}</span>
          <b className="text-sm">{fmtEur(r.compenso.amount)}</b>
          <CompensoPill status={r.compenso.status} testId={`admin-referral-compenso-${r.id}`} />
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      {open ? <Editor r={r} onSaved={onSaved} /> : null}
    </OfficeCard>
  );
}

export default function AdminReferrals({ partners, onChanged }) {
  const [filters, setFilters] = useState({ partner_id: "", esito: "", status: "", month: "", comune: "" });
  const [refs, setRefs] = useState([]);
  const load = useCallback(async () => {
    try { setRefs(await api.adminReferrals(filters)); } catch (e) { toast.error(e.message); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  const setF = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const refresh = () => { load(); onChanged(); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" data-testid="referral-filters">
        <select value={filters.partner_id} onChange={setF("partner_id")} className={sel} style={{ borderColor: OFFICE.border }} data-testid="filter-partner">
          <option value="">Tutti i partner</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{p.agency_name}</option>)}
        </select>
        <select value={filters.esito} onChange={setF("esito")} className={sel} style={{ borderColor: OFFICE.border }} data-testid="filter-esito">
          <option value="">Tutti gli esiti</option>
          {ESITI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.status} onChange={setF("status")} className={sel} style={{ borderColor: OFFICE.border }} data-testid="filter-status">
          <option value="">Tutti gli stati</option>
          <option value="new">Valide</option>
          <option value="duplicate">Contatto già presente</option>
        </select>
        <input type="month" value={filters.month} onChange={setF("month")} className={sel} style={{ borderColor: OFFICE.border }} data-testid="filter-month" />
        <input value={filters.comune} onChange={setF("comune")} placeholder="Comune" className={sel} style={{ borderColor: OFFICE.border }} data-testid="filter-comune" />
      </div>
      <div className="text-sm" style={{ color: OFFICE.muted }} data-testid="referral-count">{refs.length} segnalazioni</div>
      {refs.map((r) => <Row key={r.id} r={r} onSaved={refresh} />)}
    </div>
  );
}
